import { useState, useRef, useEffect } from "react";
import { Loader2, CheckCircle2, Clock, ScanBarcode, ClipboardPaste, AlertCircle, Banknote, ChevronLeft, ChevronRight, Store, PackageX } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  useCodEntries,
  useCodSummary,
  useMarkCodReceived,
  useScanCodReceived,
  useBulkScanCodReceived,
  type CodSummary,
  type CodSummaryAll,
} from "@/hooks/use-fulfillment";
import { useStores } from "@/hooks/use-stores";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { RETURN_REASON_LABEL, type PackageReturnReason } from "@/lib/package-return-labels";
import { MetaOrderTag } from "@/components/meta-orders/meta-badge";

function isCodSummaryAll(summary: CodSummary | CodSummaryAll): summary is CodSummaryAll {
  return "aggregated" in summary;
}

const STATUS_TABS = [
  { value: "pending", label: "Na čekanju" },
  { value: "received", label: "Primljeno" },
  { value: "returned", label: "Vraćeno" },
  { value: "all", label: "Sve" },
] as const;

export function CodPage() {
  const { data: stores } = useStores();
  const [pageStoreFilter, setPageStoreFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [scanCode, setScanCode] = useState("");
  const [scanMode, setScanMode] = useState(false);
  const [lastScanned, setLastScanned] = useState<{ orderId: string; amount: number; currency: string; storeName?: string } | null>(null);
  const [bulkText, setBulkText] = useState("");
  const [bulkCodes, setBulkCodes] = useState<string[]>([]);
  const [bulkResult, setBulkResult] = useState<{ received: number; notFound: string[]; ambiguous: string[] } | null>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);

  const showAllStores = pageStoreFilter === null;
  const { data: entries, isLoading } = useCodEntries(pageStoreFilter, {
    status: statusFilter === "all" ? undefined : statusFilter,
    page,
  });
  const { data: summary } = useCodSummary(pageStoreFilter);
  const markReceived = useMarkCodReceived();
  const scanReceived = useScanCodReceived();
  const bulkScan = useBulkScanCodReceived();

  useEffect(() => {
    if (scanMode) scanInputRef.current?.focus();
  }, [scanMode]);

  function extractCodes(text: string): string[] {
    const matches = text.match(/(?:PPX|O?X)\d{9,}/g) || [];
    return [...new Set(matches)];
  }

  function handleBulkTextChange(text: string) {
    setBulkText(text);
    setBulkCodes(extractCodes(text));
    setBulkResult(null);
  }

  async function handleBulkSubmit() {
    if (bulkCodes.length === 0) return;
    try {
      const result = await bulkScan.mutateAsync({
        codes: bulkCodes,
        ...(pageStoreFilter ? { storeId: pageStoreFilter } : {}),
      });
      const res = result as any;
      setBulkResult({
        received: res.received?.length ?? 0,
        notFound: res.notFound ?? [],
        ambiguous: res.ambiguous ?? [],
      });
      const total = res.received?.reduce((sum: number, e: any) => sum + e.amount, 0) ?? 0;
      toast.success(`${res.received?.length ?? 0} stavki primljeno — ${total.toFixed(2)} BAM`);
    } catch (e: any) {
      toast.error(e?.message || "Greška pri obradi kodova");
    }
  }

  async function handleScan(e: React.FormEvent) {
    e.preventDefault();
    const code = scanCode.trim();
    if (!code) return;

    try {
      const entry = await scanReceived.mutateAsync({
        code,
        ...(pageStoreFilter ? { storeId: pageStoreFilter } : {}),
      });
      const e2 = entry as { orderId: string; amount: number; currency: string; storeName?: string };
      setLastScanned({
        orderId: e2.orderId,
        amount: e2.amount,
        currency: e2.currency,
        storeName: e2.storeName,
      });
      const storeLabel = e2.storeName ? ` (${e2.storeName})` : "";
      toast.success(`#${e2.orderId}${storeLabel} — ${e2.amount.toFixed(2)} ${e2.currency} primljeno`);
      setScanCode("");
      scanInputRef.current?.focus();
    } catch (e: any) {
      const msg = e?.message || "Greška pri skeniranju";
      toast.error(msg);
      setScanCode("");
      setLastScanned(null);
      scanInputRef.current?.focus();
    }
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const selectablePending: any[] = entries?.data?.filter((e: any) => e.status === "pending" && !e.returnReason) ?? [];

  function toggleSelectAll() {
    const pendingIds = selectablePending.map((e) => e.id);
    const allSelected = pendingIds.every((id: string) => selected.has(id));
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pendingIds));
    }
  }

  async function handleMarkReceived() {
    if (selected.size === 0) return;
    try {
      await markReceived.mutateAsync({ ids: Array.from(selected) });
      toast.success(`${selected.size} stavki označeno kao primljeno`);
      setSelected(new Set());
    } catch (e: any) {
      toast.error(e?.message || "Greška pri označavanju primljeno");
    }
  }

  const totalPages = entries ? Math.ceil(entries.total / 25) : 0;

  const singleSummary = summary && !isCodSummaryAll(summary) ? summary : null;
  const allSummary = summary && isCodSummaryAll(summary) ? summary : null;

  function renderSummaryCards(
    data: { totalPending: number; pendingCount: number; totalReceivedThisMonth: number; totalReturned: number; returnedCount: number },
  ) {
    return (
      <div className={cn("grid gap-3", data.returnedCount > 0 ? "grid-cols-3" : "grid-cols-2")}>
        <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Clock className="h-3 w-3 shrink-0" />
            <span className="text-xs">Na čekanju</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-medium tabular-nums">{data.totalPending.toFixed(2)}</span>
            <span className="text-[10px] text-muted-foreground">BAM</span>
          </div>
          <span className="text-[10px] text-muted-foreground tabular-nums">{data.pendingCount} narudžbi</span>
        </div>
        <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <CheckCircle2 className="h-3 w-3 shrink-0" />
            <span className="text-xs">Primljeno ovaj mjesec</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-medium tabular-nums">{data.totalReceivedThisMonth.toFixed(2)}</span>
            <span className="text-[10px] text-muted-foreground">BAM</span>
          </div>
        </div>
        {data.returnedCount > 0 && (
          <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <PackageX className="h-3 w-3 shrink-0" />
              <span className="text-xs">Vraćeno</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-sm font-medium tabular-nums">{data.totalReturned.toFixed(2)}</span>
              <span className="text-[10px] text-muted-foreground">BAM</span>
            </div>
            <span className="text-[10px] text-muted-foreground tabular-nums">{data.returnedCount} paketa</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-4 max-w-5xl">
      {/* Header row */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <Banknote className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-medium">COD Praćenje</h2>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={pageStoreFilter ?? "all"}
            onValueChange={(v) => {
              setPageStoreFilter(v === "all" ? null : v);
              setPage(1);
              setSelected(new Set());
            }}
          >
            <SelectTrigger className="h-8 w-[180px] text-xs">
              <SelectValue placeholder="Trgovina" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sve trgovine</SelectItem>
              {stores?.map((store: { id: string; name: string }) => (
                <SelectItem key={store.id} value={store.id}>
                  {store.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        <Button
          variant={scanMode ? "default" : "outline"}
          size="sm"
          onClick={() => { setScanMode(!scanMode); setLastScanned(null); setBulkResult(null); }}
        >
          <ScanBarcode className="h-3.5 w-3.5 mr-1.5" />
          {scanMode ? "Zatvori" : "Skeniraj"}
        </Button>
        </div>
      </div>

      {/* Summary cards */}
      {!summary ? (
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : showAllStores && allSummary ? (
        <div className="flex flex-col gap-3">
          {renderSummaryCards(allSummary.aggregated)}
          {allSummary.perStore.length > 0 && (
            <div className="ring-1 ring-foreground/10 divide-y divide-foreground/10">
              <div className="px-3 py-2 flex items-center gap-1.5 text-muted-foreground">
                <Store className="h-3 w-3 shrink-0" />
                <span className="text-[10px] uppercase tracking-[0.15em]">Po trgovini</span>
              </div>
              {allSummary.perStore.map((store) => (
                <div
                  key={store.storeId}
                  className="px-3 py-2 flex items-center justify-between gap-3 text-xs"
                >
                  <span className="font-medium truncate">{store.storeName}</span>
                  <div className="flex items-center gap-4 shrink-0 tabular-nums text-muted-foreground">
                    <span>
                      <span className="text-foreground font-medium">{store.totalPending.toFixed(2)}</span> BAM na čekanju
                      <span className="text-foreground/40 mx-1">·</span>
                      {store.pendingCount} nar.
                    </span>
                    <span>
                      {store.totalReceivedThisMonth.toFixed(2)} BAM ovaj mj.
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : singleSummary ? (
        renderSummaryCards(singleSummary)
      ) : null}

      {/* Scanner panel */}
      {scanMode && (
        <div className="border border-foreground/15 divide-y divide-foreground/10">
          {/* Single scan */}
          <div className="p-3 flex flex-col gap-2">
            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              Skeniranje
            </span>
            <form onSubmit={handleScan} className="flex gap-2">
              <Input
                ref={scanInputRef}
                value={scanCode}
                onChange={(e) => setScanCode(e.target.value)}
                placeholder="OX017504263, X017504263 ili PPX017504263"
                className="flex-1 h-9"
                autoFocus
              />
              <Button type="submit" size="sm" className="h-9 px-4" disabled={scanReceived.isPending || !scanCode.trim()}>
                {scanReceived.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "OK"}
              </Button>
            </form>
            {lastScanned && (
              <div className="flex items-center gap-2 text-xs bg-green-500/5 border border-green-500/15 px-2.5 py-1.5">
                <CheckCircle2 className="h-3 w-3 text-green-600 shrink-0" />
                <span className="text-green-700 dark:text-green-400">
                  #{lastScanned.orderId}
                  {lastScanned.storeName ? ` (${lastScanned.storeName})` : ""} —{" "}
                  <strong>{lastScanned.amount.toFixed(2)} {lastScanned.currency}</strong>
                </span>
              </div>
            )}
          </div>

          {/* Bulk paste */}
          <div className="p-3 flex flex-col gap-2">
            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <ClipboardPaste className="h-3 w-3 inline mr-1 -mt-0.5" />
              Bulk unos
            </span>
            <textarea
              value={bulkText}
              onChange={(e) => handleBulkTextChange(e.target.value)}
              placeholder={"Zalijepi tekst sa OX ili PPX kodovima...\nOX017504263 OX017504264 PPX017504265"}
              className="w-full h-20 bg-muted/50 border border-input px-3 py-2 text-xs font-mono resize-y outline-none focus:border-ring focus:bg-transparent transition-colors placeholder:text-muted-foreground/40"
            />
            {bulkCodes.length > 0 && (
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {bulkCodes.length} {bulkCodes.length === 1 ? "kod" : "kodova"}
                    <span className="hidden sm:inline text-foreground/30 mx-1.5">|</span>
                    <span className="hidden sm:inline">{bulkCodes.slice(0, 3).join(" · ")}{bulkCodes.length > 3 ? " ..." : ""}</span>
                  </span>
                </div>
                <Button size="sm" className="h-8 shrink-0" onClick={handleBulkSubmit} disabled={bulkScan.isPending}>
                  {bulkScan.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
                  Potvrdi {bulkCodes.length}
                </Button>
              </div>
            )}
            {bulkResult && (
              <div className="flex flex-col gap-1 text-xs">
                {bulkResult.received > 0 && (
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                    <CheckCircle2 className="h-3 w-3 shrink-0" />
                    {bulkResult.received} primljeno
                  </div>
                )}
                {bulkResult.notFound.length > 0 && (
                  <div className="flex items-start gap-2 text-destructive">
                    <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                    <span>Nije pronađeno: {bulkResult.notFound.join(", ")}</span>
                  </div>
                )}
                {bulkResult.ambiguous.length > 0 && (
                  <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
                    <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                    <span>
                      Više trgovina za isti kod — odaberite trgovinu pa ponovite:{" "}
                      {bulkResult.ambiguous.join(", ")}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs + bulk action bar */}
      <div className="flex items-end justify-between gap-3">
        <div className="flex gap-0 border-b border-foreground/10">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => { setStatusFilter(tab.value); setPage(1); setSelected(new Set()); }}
              className={cn(
                "shrink-0 px-3 pb-2 pt-0.5 text-xs font-medium transition-colors relative",
                "hover:text-foreground",
                statusFilter === tab.value
                  ? "text-foreground"
                  : "text-muted-foreground"
              )}
            >
              {tab.label}
              {statusFilter === tab.value && (
                <span className="absolute bottom-0 left-3 right-3 h-px bg-foreground" />
              )}
            </button>
          ))}
        </div>

        {selected.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] tabular-nums text-muted-foreground">{selected.size} odabrano</span>
            <Button size="sm" className="h-7" onClick={handleMarkReceived} disabled={markReceived.isPending}>
              {markReceived.isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              Označi primljeno
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex flex-col gap-px">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
        </div>
      ) : !entries?.data?.length ? (
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
          <Banknote className="h-6 w-6 opacity-30" />
          <p className="text-xs">Nema zapisa</p>
        </div>
      ) : (
        <>
          <div className="border border-foreground/10 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/60 text-muted-foreground">
                  <th className="w-8 px-2 py-2 text-left">
                    <Checkbox
                      checked={selectablePending.length > 0 && selectablePending.every((e) => selected.has(e.id))}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider">Narudžba</th>
                  {showAllStores && (
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider hidden sm:table-cell">Trgovina</th>
                  )}
                  <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider hidden md:table-cell">Tracking</th>
                  <th className="px-2 py-2 text-right font-medium text-[10px] uppercase tracking-wider">Iznos</th>
                  <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider">Status</th>
                  <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider hidden sm:table-cell">Datum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-foreground/5">
                {entries.data.map((entry: any) => (
                  <tr
                    key={entry.id}
                    className={cn(
                      "transition-colors hover:bg-muted/40",
                      selected.has(entry.id) && "bg-muted/50"
                    )}
                  >
                    <td className="px-2 py-1.5">
                      {entry.status === "pending" && !entry.returnReason && (
                        <Checkbox
                          checked={selected.has(entry.id)}
                          onCheckedChange={() => toggleSelect(entry.id)}
                        />
                      )}
                    </td>
                    <td className="px-2 py-1.5 font-medium">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span>#{entry.orderId}</span>
                        {entry.metaNumber && <MetaOrderTag number={entry.metaNumber} storeNames={entry.metaStoreNames ?? []} />}
                      </div>
                    </td>
                    {showAllStores && (
                      <td className="px-2 py-1.5 text-muted-foreground hidden sm:table-cell truncate max-w-[120px]">
                        {entry.storeName || entry.storeId}
                      </td>
                    )}
                    <td className="px-2 py-1.5 font-mono text-muted-foreground hidden md:table-cell">
                      {entry.trackingNumber || <span className="text-foreground/20">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-medium">{entry.amount.toFixed(2)} <span className="text-muted-foreground font-normal">{entry.currency}</span></td>
                    <td className="px-2 py-1.5">
                      {entry.returnReason ? (
                        <Link
                          to="/fulfillment/returned"
                          search={entry.trackingNumber ? { q: entry.trackingNumber } : {}}
                          className="inline-flex items-center gap-1.5 text-red-600 dark:text-red-400 hover:underline"
                          title={entry.returnedAt ? new Date(entry.returnedAt * 1000).toLocaleDateString("bs-BA") : undefined}
                        >
                          <span className="h-1.5 w-1.5 bg-red-500 shrink-0" />
                          Vraćeno — {RETURN_REASON_LABEL[entry.returnReason as PackageReturnReason] ?? entry.returnReason}
                        </Link>
                      ) : entry.status === "pending" ? (
                        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                          <span className="h-1.5 w-1.5 bg-amber-500/80 shrink-0" />
                          Na čekanju
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-green-600 dark:text-green-500">
                          <span className="h-1.5 w-1.5 bg-green-500 shrink-0" />
                          Primljeno
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground hidden sm:table-cell tabular-nums">
                      {new Date(entry.packedAt * 1000).toLocaleDateString("bs-BA")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {entries.total} ukupno
              </span>
              <div className="flex items-center gap-1">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="h-7 w-7 flex items-center justify-center border border-foreground/10 text-muted-foreground hover:text-foreground hover:border-foreground/25 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                >
                  <ChevronLeft className="h-3 w-3" />
                </button>
                <span className="text-[10px] tabular-nums text-muted-foreground px-2">
                  {page}/{totalPages}
                </span>
                <button
                  disabled={!entries.hasNextPage}
                  onClick={() => setPage(page + 1)}
                  className="h-7 w-7 flex items-center justify-center border border-foreground/10 text-muted-foreground hover:text-foreground hover:border-foreground/25 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                >
                  <ChevronRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
